import { AccountEmployeeResolver } from '@account/account-employee.resolver';
import {
    AccountShowView,
    BenefitsShowView,
    FeedbackIndexView,
    GoalIndexView,
    JobAndPayShowView,
    JourneyIndexView,
    PaystubIndexView,
    PersonalShowView,
    RemindersIndexView,
    ReviewIndexView,
    SurveyIndexView,
} from '@account/views';
import { NgModule } from '@angular/core';
import { RouterModule } from '@angular/router';
import { Platform } from '@app/classes';
import { FeatureFlag } from '@app/enums';
import {
    CompanyModuleGuard,
    FeatureGuard,
    HasFeatureRedirectGuard,
    PermissionGuard,
    WithoutPermissionGuard,
} from '@app/guards';
import { RoutesWithOptions } from '@app/interfaces';
import { KYCKYBPreRedirectView } from '@app/views/kyc-kyb/kyc-kyb.view';
import { TimeOffCalendarView } from '@common/views';
import { EmployeeResolver as TimeOffEmployeeResolver } from '@time-off-v3/resolvers/employee.resolver';
import { ProfileView, TeamView } from '@time-off-v3/views';
import { MyDocumentsFoldersIndexView } from './views/employee/documents-v2/folders/my-documents-folders-index.view';

/** Documents */
import { EmployeeDocumentsBulkUploadView } from '@employees/views/employee/documents-v2/bulk-upload/employee-documents-bulk-upload.view';

const routes: RoutesWithOptions = [
    {
        path: '',
        resolve: {
            models: AccountEmployeeResolver,
        },
        children: [
            {
                path: 'benefits',
                component: BenefitsShowView,
            },

            {
                path: 'documents',
                canActivate: [HasFeatureRedirectGuard],
                children: [
                    {
                        path: '',
                        pathMatch: 'full',
                        redirectTo: 'folders',
                    },
                    {
                        path: 'folders',
                        children: [
                            {
                                path: '',
                                component: MyDocumentsFoldersIndexView,
                            },
                            {
                                path: ':node',
                                component: MyDocumentsFoldersIndexView,
                            },
                        ],
                    },

                    // This redirects old notifications for old documents to the new document center
                    {
                        path: ':documentId/sign',
                        redirectTo: '/profile/documents',
                    },
                    {
                        path: 'bulk-upload',
                        component: EmployeeDocumentsBulkUploadView,
                        data: {
                            isAccountView: true,
                        },
                    },
                ],
                data: {
                    feature: {
                        flags: [FeatureFlag.documentsV2],
                        redirectToReplace: ['/documents/', '/documents-v2/'],
                    },
                },
            },

            {
                path: 'documents-v2',
                canActivate: [FeatureGuard],
                data: {
                    feature: { flags: [FeatureFlag.documentsV2] },
                },
                children: [
                    {
                        path: '',
                        pathMatch: 'full',
                        redirectTo: 'folders',
                    },
                    {
                        path: 'folders',
                        children: [
                            {
                                path: '',
                                component: MyDocumentsFoldersIndexView,
                            },
                            {
                                path: ':node',
                                component: MyDocumentsFoldersIndexView,
                            },
                        ],
                    },
                    {
                        path: 'bulk-upload',
                        component: EmployeeDocumentsBulkUploadView,
                        data: {
                            isAccountView: true,
                        },
                    },
                ],
            },
            {
                path: 'performance',
                canActivate: [PermissionGuard],
                data: {
                    permission: ['accessPerformance'],
                },
                children: [
                    {
                        path: '',
                        pathMatch: 'full',
                        redirectTo: 'reviews',
                    },
                    {
                        path: 'reviews',
                        component: ReviewIndexView,
                    },
                    {
                        path: 'goals',
                        component: GoalIndexView,
                    },
                    {
                        path: 'feedbacks',
                        component: FeedbackIndexView,
                    },
                ],
            },
            {
                path: 'surveys',
                component: SurveyIndexView,
                canActivate: [PermissionGuard],
                data: {
                    permission: ['accessPerformance'],
                },
            },
            {
                path: 'time-off',
                component: ProfileView,
                canActivate: [WithoutPermissionGuard],
                resolve: {
                    models: TimeOffEmployeeResolver,
                },
                data: {
                    cannot: ['administrate'],
                    withoutPermissionRedirectTo: ['/time-off', 'v3', 'requests'],
                },
            },
            {
                path: 'time-off/v3/team',
                component: TeamView,
                canActivate: [PermissionGuard, WithoutPermissionGuard],
                data: {
                    cannot: ['administrate'],
                    withoutPermissionRedirectTo: ['/time-off', 'v3', 'requests'],
                    permission: ['manageDirectReports', 'manageDirectAndIndirectReports'],
                    canActivateRedirectTo: ['/profile', 'time-off', 'v3'],
                },
            },
            {
                /**
                 * @deprecated
                 *
                 * Redirect to solo scoped route below
                 */
                path: 'time-off/calendar',
                redirectTo: 'profile/time-off/calendar',
            },
            {
                path: 'calendar/time-off',
                component: TimeOffCalendarView,
            },

            {
                path: 'paystubs',
                component: PaystubIndexView,
            },

            // Account Prefix
            {
                path: '',
                redirectTo: 'personal',
                pathMatch: 'full',
            },
            {
                path: 'personal',
                component: PersonalShowView,
            },
            {
                path: 'job-and-pay',
                component: JobAndPayShowView,
            },
            {
                path: 'account',
                component: AccountShowView,
            },
            {
                path: 'reminders',
                component: RemindersIndexView,
            },
            {
                path: 'journey',
                component: JourneyIndexView,
            },
            {
                path: 'benefits',
                redirectTo: 'profile/benefits',
            },
            {
                path: 'performance',
                redirectTo: 'profile/performance',
            },
            {
                path: 'time-off',
                redirectTo: 'profile/time-off',
            },
            {
                path: 'time-off/calendar',
                redirectTo: 'profile/time-off/calendar',
            },
            {
                path: 'paystubs',
                redirectTo: 'profile/paystubs',
            },
            {
                path: 'verify-your-identity',
                component: KYCKYBPreRedirectView,
                canActivate: [PermissionGuard, FeatureGuard, CompanyModuleGuard],
                data: {
                    permission: 'accessPayroll',
                    feature: { flags: [FeatureFlag.KYC_KYB] },
                    scope: 'KYC',
                    companyModule: Platform.modules.payroll,
                },
            },
        ],
    },
];

@NgModule({
    imports: [RouterModule.forChild(routes)],
    exports: [RouterModule],
})
export class AccountRouterModule {}
